/** @format */

import React from 'react';

import clsx from 'clsx';
import {Box, FormControlLabel, Tooltip, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import GreenCheckbox from 'components/checkboxes/GreenCheckbox';

import {ConcreteSensorGroups} from 'helpers/sensorGroupsHelpers';

import {YalperChecksSensorGroup} from 'graphql/generated/types';

import {useStyles} from './styles';

interface SensorGroupsSelectorProps {
  sensorGroups: ConcreteSensorGroups;
  value: ReadonlyArray<YalperChecksSensorGroup>;
  onChange: (next: YalperChecksSensorGroup[]) => void;
}

type GroupDescriptor = {
  key: YalperChecksSensorGroup;
  responseKey: keyof ConcreteSensorGroups;
  label: React.ReactNode;
};

export const SensorGroupsSelector = ({
  sensorGroups,
  value,
  onChange,
}: SensorGroupsSelectorProps) => {
  const classes = useStyles();
  const {t} = useTranslation();

  const groups: GroupDescriptor[] = [
    {
      key: 'RED',
      responseKey: 'red',
      label: <Trans>Критичные</Trans>,
    },
    {
      key: 'YELLOW',
      responseKey: 'yellow',
      label: <Trans>Обычные</Trans>,
    },
    {
      key: 'GRAY',
      responseKey: 'gray',
      label: <Trans>Некритичные</Trans>,
    },
  ];

  const handleToggle = (groupKey: YalperChecksSensorGroup) => () => {
    if (value.includes(groupKey)) {
      onChange(value.filter(v => v !== groupKey));
    } else {
      onChange([...value, groupKey]);
    }
  };

  return (
    <Box className={classes.groupsSelectorWrapper}>
      <Typography className={classes.groupsTitle}>
        <Trans>Группы сенсоров</Trans>
      </Typography>

      <Box className={classes.groupsRow}>
        {groups.map(group => {
          const isEmpty = sensorGroups[group.responseKey].length === 0;
          const isChecked = value.includes(group.key);

          const checkbox = (
            <FormControlLabel
              className={clsx(classes.groupItem, isEmpty && classes.groupItemDisabled)}
              control={
                <GreenCheckbox
                  disableRipple
                  checked={isChecked}
                  disabled={isEmpty}
                  inputProps={{'aria-label': `sensor-group-${group.key.toLowerCase()}`}}
                  onChange={handleToggle(group.key)}
                />
              }
              label={
                <Typography component='span' className={classes.groupLabel}>
                  {group.label}
                </Typography>
              }
            />
          );

          if (isEmpty) {
            return (
              <Tooltip
                key={group.key}
                title={t('В этой группе нет сенсоров', 'В этой группе нет сенсоров')}
                enterTouchDelay={0}
                leaveTouchDelay={3000}
              >
                <Box component='span'>{checkbox}</Box>
              </Tooltip>
            );
          }

          return <React.Fragment key={group.key}>{checkbox}</React.Fragment>;
        })}
      </Box>
    </Box>
  );
};
